import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Banknote, Clock, FileCheck, Shield, ArrowRight, TrendingUp, Building2 } from "lucide-react";
import CEEExplainer from "@/components/CEEExplainer";
import ComparisonTable from "@/components/ComparisonTable";
import FAQAccordion from "@/components/FAQAccordion";
import ContactSection from "@/components/ContactSection";

const valueProps = [
  { icon: Banknote, title: "Jusqu'à 100 % financé", desc: "Sur certaines opérations standardisées cumulées avec d'autres aides — sans reste à charge." },
  { icon: Clock, title: "Versement sécurisé", desc: "Valorisation fixée dès la signature, paiement après contrôle qualité travaux." },
  { icon: FileCheck, title: "Dossier PNCEE conforme", desc: "Constitution, dépôt, archivage et défense en cas de contrôle — 0 redressement à ce jour." },
  { icon: Shield, title: "Sécurité juridique", desc: "Contrats clairs, garanties contractuelles, conformité Décret Tertiaire & RE2020." },
];

const operationFlow = [
  { step: "01", title: "Vérification d'éligibilité", desc: "Confirmation que votre opération entre dans le catalogue CEE (fiches BAR / BAT / IND / RES / TRA / AGRI)." },
  { step: "02", title: "Calcul du volume CEE", desc: "Détermination du nombre de kWh cumac générés selon la durée de vie de l'équipement et le secteur géographique." },
  { step: "03", title: "Valorisation financière", desc: "Multiplication par le cours du CEE du moment — vous recevez une offre nette en euros." },
  { step: "04", title: "Engagement avant travaux", desc: "Signature de l'engagement CEE OBLIGATOIREMENT avant tout devis travaux signé." },
  { step: "05", title: "Réalisation & contrôle", desc: "Travaux par partenaires RGE, contrôle qualité ECONATION, attestation sur l'honneur." },
  { step: "06", title: "Dépôt PNCEE & paiement", desc: "Constitution du dossier complet, dépôt, validation, versement de la prime." },
];

export default function Financement() {
  return (
    <div data-testid="financement-page">
      {/* HERO */}
      <section className="relative overflow-hidden bg-slate-50 text-slate-900">
        <div className="absolute inset-0 bg-grid-soft opacity-[0.06] pointer-events-none" />
        <div className="absolute -top-32 -right-32 h-[520px] w-[520px] rounded-full bg-[#10B981]/20 blur-3xl" />
        <div className="absolute -bottom-32 -left-32 h-[420px] w-[420px] rounded-full bg-[#0055A4]/15 blur-3xl" />

        <div className="relative max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 pt-40 md:pt-48 pb-24 md:pb-32">
          <div className="max-w-4xl">
            <div className="text-[11px] font-semibold tracking-[0.28em] text-[#10B981] uppercase mb-5">Financement CEE</div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-7xl font-bold tracking-[-0.025em] text-balance leading-[1.02]">
              Le dispositif d&apos;État qui finance vos travaux d&apos;efficacité énergétique.
            </h1>
            <p className="mt-7 text-base md:text-xl text-slate-600 max-w-2xl leading-relaxed">
              Depuis 2006, l&apos;État impose aux fournisseurs d&apos;énergie un volume d&apos;économies à réaliser
              chaque période. Pour atteindre leurs objectifs, ils financent vos travaux. Voilà comment ça marche.
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <Link to="/eligibilite" data-testid="financement-cta-eligibility">
                <Button className="bg-[#0055A4] hover:bg-[#003D7A] text-slate-900 font-semibold py-7 px-9 rounded-full shadow-[0_20px_50px_-20px_rgba(249,115,22,0.6)]">
                  Vérifier mon éligibilité <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>
          </div>

          <div className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-4">
            {valueProps.map((v) => (
              <div key={v.title} className="bg-slate-50 backdrop-blur-md border border-slate-200 rounded-2xl p-6">
                <div className="h-10 w-10 rounded-lg bg-[#10B981]/15 grid place-items-center text-[#10B981] mb-4">
                  <v.icon className="h-4 w-4" strokeWidth={2} />
                </div>
                <div className="font-display font-semibold mb-1.5">{v.title}</div>
                <div className="text-xs text-slate-500 leading-relaxed">{v.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <CEEExplainer />

      {/* PROCESS */}
      <section data-testid="cee-process" className="bg-white">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-24 md:py-32">
          <div className="max-w-3xl mb-14">
            <div className="text-[11px] font-semibold tracking-[0.28em] text-[#10B981] uppercase mb-5">Anatomie d&apos;un dossier CEE</div>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-[#0F172A] tracking-tight text-balance leading-[1.05]">
              6 étapes administratives, 0 charge mentale pour vous.
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {operationFlow.map((s) => (
              <div key={s.step} data-testid={`financement-step-${s.step}`} className="relative bg-white border border-black/[0.06] rounded-2xl p-7 hover:border-[#10B981]/40 hover:shadow-[0_18px_50px_-30px_rgba(16,185,129,0.4)] transition-all">
                <div className="absolute -top-3 -left-3 h-12 w-12 rounded-xl bg-white text-slate-900 grid place-items-center font-display font-bold text-sm shadow-md">{s.step}</div>
                <h3 className="font-display text-lg font-semibold text-[#0F172A] mb-2 mt-4">{s.title}</h3>
                <p className="text-sm text-[#475569] leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SIMULATION */}
      <section data-testid="financement-simulation" className="bg-[#F8FAFC] border-y border-black/5">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-24 md:py-32 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <div className="text-[11px] font-semibold tracking-[0.28em] text-[#10B981] uppercase mb-5">Exemples chiffrés</div>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-[#0F172A] tracking-tight text-balance leading-[1.05] mb-6">
              Combien rapporte concrètement un projet CEE ?
            </h2>
            <p className="text-[#475569] leading-relaxed mb-8">
              Quelques ordres de grandeur tirés de programmes ECONATION réels. Chiffres validés par
              le PNCEE, hors aides cumulables additionnelles.
            </p>
            <Link to="/eligibilite" data-testid="financement-simulate-cta">
              <Button className="bg-[#10B981] hover:bg-[#059669] text-slate-900 font-semibold py-7 px-9 rounded-full">
                Simuler mon dossier <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
          <div className="lg:col-span-6 space-y-4">
            {[
              { icon: Building2, label: "Hôtel 4★, 120 chambres", op: "Pompe à chaleur + isolation toiture", value: "184 000 €" },
              { icon: TrendingUp, label: "Site industriel agroalimentaire", op: "Récupération de chaleur fatale", value: "612 000 €" },
              { icon: Banknote, label: "Copropriété 184 lots", op: "PAC collective + ITE", value: "417 000 €" },
            ].map((ex) => (
              <div key={ex.label} className="bg-white rounded-2xl border border-black/[0.06] p-6 flex items-center gap-5">
                <div className="h-12 w-12 rounded-xl bg-white text-slate-900 grid place-items-center shrink-0">
                  <ex.icon className="h-5 w-5" strokeWidth={1.75} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-[#94A3B8] uppercase tracking-widest">{ex.label}</div>
                  <div className="font-display text-base font-semibold text-[#0F172A] mt-1">{ex.op}</div>
                </div>
                <div className="font-display text-2xl md:text-3xl font-bold text-[#10B981] whitespace-nowrap">{ex.value}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <ComparisonTable />
      <FAQAccordion />
      <ContactSection />
    </div>
  );
}
